import Vue from "vue";
import DateSelector from "../../components/DateSelector.vue";
import ChoicesField from "../../components/ChoicesField.vue";
import AvailabilitySummary from "../../components/AvailabilitySummary.vue";
import AvailabilityBuilder from "../../components/AvailabilityBuilder.vue";
import WipeAvailabilities from "../../components/WipeAvailabilities.vue";
import { DateTime } from "luxon";
import { formattedDate, localTime, localTimezone } from "../../utils";
import { AvailabilityEvent, SelectOption } from "../../types";
import sortBy from "lodash/sortBy";
import axios from "axios";
import { TIME_FORMAT, HOUR_CHOICE_START } from "../../constants";

export interface AvailabilitiesForDate {
  availabilities: AvailabilityEvent[];
  date: DateTime;
}

export const PageComponent = Vue.extend({
  created() {
    this.loadAvailabilities();

    setInterval(() => {
      if (new Date().getSeconds() === 0) {
        this.updateDateWindow();
      }
    }, 1000);

    this.updateDateWindow();
  },

  watch: {
    currentDate(newValue: DateTime, oldValue: DateTime) {
      if (newValue.month !== oldValue.month) {
        this.loadAvailabilities();
      }
    }
  },

  data() {
    return {
      minimumDate: localTime(),
      currentDate: localTime(),
      availabilities: [] as AvailabilityEvent[],
      saving: false,
      buildingAvailability: false,
      buildingExclusion: false
    };
  },

  computed: {
    isValid(): boolean {
      return true;
    },

    availabilitiesByDate(): AvailabilitiesForDate[] {
      const summaries = this.availabilities.reduce(
        (acc: AvailabilitiesForDate[], availability) => {
          const date = DateTime.fromISO(availability.starts_at, { zone: "UTC" })
            .setZone(localTimezone)
            .startOf("day");
          const existingIndex = acc.findIndex(
            (m: AvailabilitiesForDate) => m.date.ordinal == date.ordinal
          );

          if (existingIndex >= 0) {
            acc[existingIndex].availabilities.push(availability);
          } else {
            acc.push({ date, availabilities: [availability] });
          }

          return acc;
        },
        []
      );

      return sortBy(summaries, "date");
    },

    availabilityChoices(): SelectOption[] {
      // based on a timezone change, the earliest hour for the day may be earlier
      // the the one we'd like to normally start at. First, consider the selected
      // availabilities for the day.Availability
      let minHour = this.availabilities
        .map(a =>
          DateTime.fromISO(a.starts_at, { zone: "UTC" }).setZone(localTimezone)
        )
        .filter(date => date.ordinal == this.currentDate.ordinal)
        .reduce(
          (acc: number, date: DateTime) => Math.min(acc, date.hour),
          HOUR_CHOICE_START
        );

      // Next, consider the current time
      if (this.currentDate.ordinal === this.minimumDate.ordinal) {
        minHour = Math.max(
          DateTime.local().setZone(localTimezone).hour + 1,
          minHour
        );
      }

      let currentTime = this.currentDate.set({
        hour: minHour,
        minute: 0,
        second: 0,
        millisecond: 0
      });
      const choices: SelectOption[] = [];

      while (currentTime.ordinal == this.currentDate.ordinal) {
        choices.push({
          label: currentTime.toFormat(TIME_FORMAT),
          key: currentTime.toISO() as string,
          value: currentTime.setZone("UTC")
        });

        currentTime = currentTime.plus({ hour: 1 });
      }

      return choices;
    },

    datesWithAvailability(): DateTime[] {
      return this.availabilitiesByDate.map(summary =>
        summary.date.setZone("UTC")
      );
    }
  },

  methods: {
    formattedDate,

    toggleAvailabilityBuilder() {
      this.buildingAvailability = !this.buildingAvailability;
    },

    toggleExclusionBuilder() {
      this.buildingExclusion = !this.buildingExclusion;
    },

    updateDateWindow() {
      this.minimumDate = localTime();

      if (this.minimumDate >= this.currentDate) {
        this.currentDate = this.minimumDate;
      }
    },

    goToDate(date: DateTime) {
      this.currentDate = date;
      WipeAvailabilities;
      this.loadAvailabilities();
    },

    refreshSchedule() {
      this.loadAvailabilities();
    },

    async loadAvailabilities() {
      this.availabilities = [];
      const now = this.currentDate.setZone("UTC");

      const {
        data: { data: availabilities }
      } = await axios.get("/ajax/availabilities", {
        params: {
          year: now.year,
          month: now.month
        }
      });

      this.availabilities = availabilities;
    },

    /**
     * Create an availability exclusion for a particular hour.
     * @param moment
     */
    async excludeTime(moment: DateTime) {
      const start = moment.set({ minute: 0, second: 0, millisecond: 0 });
      const payload = {
        starts_at: start.toISO(),
        ends_at: start.plus({ hour: 1 }).toISO(),
        recurrence: "none"
      };

      const index = this.availabilities.findIndex((a: AvailabilityEvent) => {
        console.log(
          DateTime.fromISO(a.starts_at, { zone: "UTC" }).toISO(),
          start.toISO()
        );
        return DateTime.fromISO(a.starts_at, { zone: "UTC" }).equals(start);
      });

      if (index === -1) {
        throw new Error("BAD MATCHING");
      }

      const [availabilityEvent] = this.availabilities.splice(index, 1);

      try {
        await axios.post("/ajax/availability-exclusions", payload);
      } catch {
        // restore the event on failure
        this.availabilities.push(availabilityEvent);
      }
    }
  },

  components: {
    DateSelector,
    ChoicesField,
    AvailabilitySummary,
    AvailabilityBuilder,
    WipeAvailabilities
  }
});
